'use client';

import { useCallback, useEffect, type JSX } from 'react';

import { Text } from '@videoblocks/design-system';

import { ConfirmCreditSpendModal } from '../../_shared/_components/Modals/ConfirmCreditSpendModal';
import { EnterpriseFeatureAccessModal } from '../../_shared/_components/Modals/EnterpriseFeatureAccessModal';
import { EnterpriseInsufficientCreditsModal } from '../../_shared/_components/Modals/EnterpriseInsufficientCreditsModal';
import { InsufficientCreditsModal } from '../../_shared/_components/Modals/InsufficientCreditsModal';
import { RegenerateConfirmationModal } from '../../_shared/_components/Modals/RegenerateConfirmationModal';
import { AiToolkitToast } from '../../_shared/_components/Toast/AiToolkitToast';
import { AspectRatioSelectField } from '../../_shared/_components/forms/AspectRatioSelectField';
import { FileUploadInputField } from '../../_shared/_components/forms/FileUploadInputField';
import { ModelSelectField } from '../../_shared/_components/forms/ModelSelectField';
import { ResolutionSelectField } from '../../_shared/_components/forms/ResolutionSelectField';
import { TextAreaInputField } from '../../_shared/_components/forms/TextAreaInputField';
import { useAiToolkitContext } from '../../_shared/_context/AiToolkitContext';
import {
    IMAGE_GENERATION_MAX_FILE_SIZE_BYTES,
    IMAGE_GENERATION_MODEL_OPTIONS,
    MAX_PROMPT_LENGTH,
} from '../../_shared/_context/constants';
import { AI_TOOLKIT_CATEGORIES } from '../../_shared/_context/types';
import { AiToolkitSidePanel } from '../../_shared/_layouts/AiToolkitSidePanel';
import { useImageGenerationForm } from './useImageGenerationForm';

export const ImageGenerationSidePanel = (): JSX.Element => {
    const {
        formState,
        isValid,
        promptState,
        promptHelperText,
        isSubmitDisabled,
        calculatedCost,
        onPromptChange,
        onPromptBlur,
        onAspectRatioChange,
        onResolutionChange,
        onReferenceImagesSelect,
        onReferenceImageRemove,
        onFileError,
        onSubmit,
        onConfirm,
    } = useImageGenerationForm();
    const { activeTask, acknowledgeTask, submitStates, dispatchConfirmationModal, credits, isEnterprise } =
        useAiToolkitContext();
    const { status } = submitStates[AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION];
    const isSuccess = status === 'success';
    const isFormDisabled = !!activeTask && activeTask.status === 'inProgress';
    const isGenerateButtonDisabled = isSubmitDisabled || isFormDisabled;
    const showOtherTaskToast = activeTask !== null && activeTask.category !== AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION;

    useEffect(() => {
        if (status === 'success' || status === 'error') {
            acknowledgeTask(AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION, status);
        }
    }, [status, acknowledgeTask]);

    const handleSubmit = useCallback((): void => {
        if (calculatedCost > credits) {
            dispatchConfirmationModal({
                type: 'OPEN_MODAL',
                modal: {
                    type: isEnterprise ? 'entInsufficientCredits' : 'insufficientCredits',
                    category: AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION,
                },
            });
            return;
        }
        if (isSuccess && isValid) {
            dispatchConfirmationModal({
                type: 'OPEN_MODAL',
                modal: { type: 'regenerate', category: AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION },
            });
        } else {
            onSubmit();
        }
    }, [calculatedCost, credits, isEnterprise, dispatchConfirmationModal, isSuccess, onSubmit, isValid]);

    return (
        <AiToolkitSidePanel
            aiToolkitCategory={AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION}
            onSubmit={handleSubmit}
            isSubmitDisabled={isGenerateButtonDisabled}
        >
            <>
                <div className="w-full flex flex-col gap-6">
                    <div className="hidden md:block shrink-0 dark:bg-neutral-dark-200 p-3 rounded-sm">
                        <Text asChild fontSize="sm" className="text-center">
                            <h2>Image Generator</h2>
                        </Text>
                    </div>
                    <TextAreaInputField
                        placeholder="Describe the image you want to generate. Be specific about subject, style, lighting, composition, colors, and mood..."
                        maxLength={MAX_PROMPT_LENGTH}
                        value={formState.prompt}
                        state={promptState}
                        helperText={promptHelperText}
                        onChange={onPromptChange}
                        onBlur={onPromptBlur}
                        disabled={isFormDisabled}
                    />
                    <FileUploadInputField
                        fileInputClassName="min-w-0 w-full"
                        acceptOptions={['.jpeg', '.jpg', '.png', '.webp']}
                        toolkitCategory={AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION}
                        label="Reference Images (optional)"
                        multiple
                        maxFiles={3}
                        maxSizeBytes={IMAGE_GENERATION_MAX_FILE_SIZE_BYTES}
                        value={formState.referenceImages}
                        onFileSelect={onReferenceImagesSelect}
                        onFileRemoveAtIndex={onReferenceImageRemove}
                        onFileError={onFileError}
                        disabled={isFormDisabled}
                    />
                    <ModelSelectField
                        modelValue={formState.model}
                        modelVersion={IMAGE_GENERATION_MODEL_OPTIONS[0].version}
                        modelName={IMAGE_GENERATION_MODEL_OPTIONS[0].name}
                        modelDescription={IMAGE_GENERATION_MODEL_OPTIONS[0].description}
                        className="w-full"
                        disabled={true}
                    />
                    <div className="w-full flex gap-3">
                        <AspectRatioSelectField
                            aiToolkitCategory={AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION}
                            triggerClassName="w-full min-w-0"
                            value={formState.aspectRatio}
                            onValueChange={onAspectRatioChange}
                            disabled={isFormDisabled}
                        />
                        <ResolutionSelectField
                            aiToolkitCategory={AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION}
                            triggerClassName="w-full min-w-0"
                            value={formState.resolution}
                            onValueChange={onResolutionChange}
                            disabled={isFormDisabled}
                        />
                    </div>
                </div>
                <AiToolkitToast open={showOtherTaskToast} activeTask={activeTask} />
                <ConfirmCreditSpendModal onConfirm={onConfirm} />
                <InsufficientCreditsModal />
                <EnterpriseInsufficientCreditsModal />
                <EnterpriseFeatureAccessModal />
                <RegenerateConfirmationModal onConfirm={onConfirm} />
            </>
        </AiToolkitSidePanel>
    );
};
